import React, { useState, useEffect, useRef } from 'react';
import { BrowserRouter, Routes, Route, NavLink } from 'react-router-dom';
import { io } from 'socket.io-client';
import { Toaster, toast } from 'react-hot-toast';
import axios from 'axios';

// ── AXIOS AUTH INTERCEPTORS ──────────────────────────────
axios.interceptors.request.use(cfg => {
  const t = localStorage.getItem('cc_token');
  if (t) cfg.headers.Authorization = `Bearer ${t}`;
  return cfg;
});
axios.interceptors.response.use(r => r, err => {
  if (err.response?.status === 401) {
    localStorage.removeItem('cc_token');
    localStorage.removeItem('cc_user');
    window.location.reload();
  }
  return Promise.reject(err);
});

const socket = io(window.location.origin);

// ── GLOBAL STYLES ────────────────────────────────────────
const S = document.createElement('style');
S.textContent = `
@import url('https://fonts.googleapis.com/css2?family=IBM+Plex+Mono:wght@400;600&family=IBM+Plex+Sans:wght@400;500;600;700&display=swap');
*{margin:0;padding:0;box-sizing:border-box}
:root{
  --bg:#080d14;--bg2:#0d1520;--bg3:#111b2a;--border:#1e2d40;--border2:#243549;
  --txt:#c8d8e8;--txt2:#6b8aaa;--txt3:#3d5570;
  --indigo:#4f8ef7;--indigo2:#3a6fd4;--green:#22d47a;--red:#ef5050;--amber:#f5a623;--cyan:#00c8ff;
}
html,body,#root{height:100%;background:var(--bg);color:var(--txt);font-family:'IBM Plex Sans',sans-serif}
::-webkit-scrollbar{width:4px;height:4px}::-webkit-scrollbar-track{background:var(--bg2)}::-webkit-scrollbar-thumb{background:var(--border2);border-radius:2px}
.layout{display:flex;height:100vh;overflow:hidden}
.sidebar{width:220px;background:var(--bg2);border-right:1px solid var(--border);display:flex;flex-direction:column;flex-shrink:0}
.logo{padding:20px 16px 16px;border-bottom:1px solid var(--border);display:flex;align-items:center;gap:10px}
.logo-icon{width:34px;height:34px;background:linear-gradient(135deg,var(--indigo),var(--cyan));border-radius:8px;display:flex;align-items:center;justify-content:center;font-size:18px}
.logo-text{font-size:15px;font-weight:700;letter-spacing:-.3px}
.logo-sub{font-size:10px;color:var(--txt3);font-family:'IBM Plex Mono',monospace;margin-top:1px}
.conn-bar{margin:10px;padding:8px 12px;border-radius:6px;font-size:11px;font-family:'IBM Plex Mono',monospace;display:flex;align-items:center;gap:6px}
.conn-bar.on{background:rgba(34,212,122,.08);color:var(--green);border:1px solid rgba(34,212,122,.2)}
.conn-bar.off{background:rgba(239,80,80,.08);color:var(--red);border:1px solid rgba(239,80,80,.2)}
.live-pill{margin:0 10px 6px;padding:6px 12px;border-radius:6px;background:rgba(79,142,247,.1);border:1px solid rgba(79,142,247,.2);color:var(--indigo);font-size:11px;font-family:'IBM Plex Mono',monospace;animation:pulse 2s infinite}
@keyframes pulse{0%,100%{opacity:1}50%{opacity:.6}}
.nav{flex:1;overflow-y:auto;padding:6px 8px}
.nav-section{font-size:10px;color:var(--txt3);font-family:'IBM Plex Mono',monospace;letter-spacing:1px;padding:10px 8px 4px;text-transform:uppercase}
.nav a{display:flex;align-items:center;gap:9px;padding:8px 10px;border-radius:6px;color:var(--txt2);text-decoration:none;font-size:13px;margin-bottom:2px;transition:all .12s}
.nav a:hover{background:rgba(79,142,247,.08);color:var(--txt)}
.nav a.active{background:rgba(79,142,247,.15);color:var(--indigo);font-weight:600}
.nav a .ni{width:16px;text-align:center;font-size:14px}
.main{flex:1;overflow-y:auto;padding:24px}
.page-header{display:flex;align-items:flex-start;justify-content:space-between;margin-bottom:24px}
.page-title{font-size:22px;font-weight:700;letter-spacing:-.4px}
.page-sub{font-size:13px;color:var(--txt2);margin-top:3px}
.actions{display:flex;gap:8px;align-items:center;flex-wrap:wrap}
.grid{display:grid;gap:14px}
.g2{grid-template-columns:1fr 1fr}
.g3{grid-template-columns:1fr 1fr 1fr}
.g4{grid-template-columns:repeat(4,1fr)}
.g-auto{grid-template-columns:repeat(auto-fit,minmax(160px,1fr))}
.card{background:var(--bg2);border:1px solid var(--border);border-radius:10px;padding:18px}
.card-title{font-size:13px;font-weight:600;margin-bottom:14px;color:var(--txt);display:flex;align-items:center;gap:7px}
.stat-card{background:var(--bg2);border:1px solid var(--border);border-radius:10px;padding:16px}
.stat-label{font-size:11px;color:var(--txt3);font-family:'IBM Plex Mono',monospace;margin-bottom:6px;letter-spacing:.5px}
.stat-val{font-size:28px;font-weight:700;line-height:1}
.stat-sub{font-size:11px;color:var(--txt3);margin-top:4px}
table{width:100%;border-collapse:collapse}
th{text-align:left;padding:9px 14px;font-size:10px;color:var(--txt3);font-family:'IBM Plex Mono',monospace;letter-spacing:.8px;text-transform:uppercase;border-bottom:1px solid var(--border)}
td{padding:10px 14px;border-bottom:1px solid rgba(30,45,64,.6);font-size:13px}
tr:last-child td{border-bottom:none}
tr:hover td{background:rgba(79,142,247,.03)}
.badge{display:inline-flex;align-items:center;gap:4px;padding:3px 9px;border-radius:100px;font-size:11px;font-weight:600;font-family:'IBM Plex Mono',monospace}
.badge.success{background:rgba(34,212,122,.12);color:var(--green)}
.badge.failed{background:rgba(239,80,80,.12);color:var(--red)}
.badge.pending{background:rgba(245,166,35,.12);color:var(--amber)}
.badge.calling{background:rgba(79,142,247,.12);color:var(--indigo)}
.badge.completed{background:rgba(107,138,170,.1);color:var(--txt2)}
.badge.inbound{background:rgba(0,200,255,.1);color:var(--cyan)}
.btn{display:inline-flex;align-items:center;gap:6px;padding:8px 16px;border-radius:7px;border:none;font-size:13px;font-weight:600;cursor:pointer;transition:all .12s;font-family:'IBM Plex Sans',sans-serif;white-space:nowrap}
.btn-primary{background:var(--indigo);color:#fff}.btn-primary:hover{background:var(--indigo2)}
.btn-green{background:var(--green);color:#000}.btn-green:hover{filter:brightness(1.1)}
.btn-red{background:var(--red);color:#fff}.btn-red:hover{filter:brightness(1.1)}
.btn-ghost{background:transparent;color:var(--txt2);border:1px solid var(--border)}.btn-ghost:hover{border-color:var(--indigo);color:var(--indigo)}
.btn-amber{background:var(--amber);color:#000}.btn-amber:hover{filter:brightness(1.1)}
.btn-sm{padding:5px 11px;font-size:12px}.btn-xs{padding:3px 8px;font-size:11px}
.btn:disabled{opacity:.4;cursor:not-allowed}
input,textarea,select{background:var(--bg3);border:1px solid var(--border);color:var(--txt);padding:9px 12px;border-radius:7px;font-size:13px;width:100%;outline:none;font-family:'IBM Plex Sans',sans-serif;transition:border-color .12s}
input:focus,textarea:focus,select:focus{border-color:var(--indigo)}
textarea{resize:vertical;min-height:100px}
label{font-size:11px;color:var(--txt2);display:block;margin-bottom:5px;font-family:'IBM Plex Mono',monospace;letter-spacing:.3px}
.fg{margin-bottom:14px}
.empty{text-align:center;color:var(--txt3);padding:32px;font-size:13px}
.mono{font-family:'IBM Plex Mono',monospace;font-size:12px}
.tag{display:inline-block;padding:2px 8px;border-radius:4px;font-size:10px;font-family:'IBM Plex Mono',monospace;background:var(--bg3);border:1px solid var(--border);color:var(--txt2)}
.separator{height:1px;background:var(--border);margin:14px 0}
.log-entry{padding:6px 10px;border-bottom:1px solid rgba(30,45,64,.4);font-size:12px;font-family:'IBM Plex Mono',monospace;display:flex;gap:10px}
.log-time{color:var(--txt3);flex-shrink:0;width:70px}
.log-msg{color:var(--txt);flex:1;word-break:break-all}
.log-in{border-left:2px solid var(--cyan)}.log-ai{border-left:2px solid var(--green)}.log-sys{border-left:2px solid var(--txt3)}
.modal-overlay{position:fixed;inset:0;background:rgba(0,0,0,.6);z-index:100;display:flex;align-items:center;justify-content:center;backdrop-filter:blur(4px)}
.modal{background:var(--bg2);border:1px solid var(--border2);border-radius:12px;padding:24px;width:100%;max-width:520px;max-height:80vh;overflow-y:auto}
.modal-title{font-size:17px;font-weight:700;margin-bottom:18px}
.toggle{position:relative;display:inline-block;width:44px;height:24px}
.toggle input{opacity:0;width:0;height:0}
.toggle-slider{position:absolute;cursor:pointer;inset:0;background:var(--bg3);border:1px solid var(--border);border-radius:24px;transition:.2s}
.toggle-slider:before{position:absolute;content:"";height:16px;width:16px;left:3px;bottom:3px;background:var(--txt3);border-radius:50%;transition:.2s}
input:checked+.toggle-slider{background:rgba(34,212,122,.2);border-color:var(--green)}
input:checked+.toggle-slider:before{transform:translateX(20px);background:var(--green)}
`;
document.head.appendChild(S);

const fmt = d => d ? new Date(d).toLocaleString('tr-TR') : '-';
const fmtTime = d => d ? new Date(d).toLocaleTimeString('tr-TR') : '-';

// ── LOGIN PAGE ───────────────────────────────────────────
function LoginPage({ onLogin }) {
  const [form, setForm] = useState({ username: '', password: '' });
  const [loading, setLoading] = useState(false);

  const login = async () => {
    if (!form.username || !form.password) return toast.error('Kullanıcı adı ve şifre gerekli');
    setLoading(true);
    try {
      const r = await axios.post('/api/auth/login', form);
      localStorage.setItem('cc_token', r.data.token);
      onLogin(r.data.user);
    } catch(e) {
      toast.error(e.response?.data?.error || 'Giriş hatası');
    }
    setLoading(false);
  };

  return (
    <div style={{ height:'100vh', display:'flex', alignItems:'center', justifyContent:'center', background:'var(--bg)' }}>
      <div style={{ width:380, background:'var(--bg2)', border:'1px solid var(--border)', borderRadius:16, padding:36 }}>
        <div style={{ textAlign:'center', marginBottom:32 }}>
          <div style={{ width:60, height:60, background:'linear-gradient(135deg,var(--indigo),var(--cyan))', borderRadius:16, display:'flex', alignItems:'center', justifyContent:'center', fontSize:32, margin:'0 auto 16px' }}>📞</div>
          <div style={{ fontSize:22, fontWeight:700 }}>AI Çağrı Merkezi</div>
          <div style={{ fontSize:11, color:'var(--txt3)', marginTop:6, fontFamily:'IBM Plex Mono' }}>CALLYSTA v3</div>
        </div>
        <div className="fg">
          <label>KULLANICI ADI</label>
          <input value={form.username} onChange={e=>setForm(p=>({...p,username:e.target.value}))} placeholder="callysta3" autoFocus
            onKeyDown={e=>e.key==='Enter'&&login()} />
        </div>
        <div className="fg">
          <label>ŞİFRE</label>
          <input type="password" value={form.password} onChange={e=>setForm(p=>({...p,password:e.target.value}))} placeholder="••••••••"
            onKeyDown={e=>e.key==='Enter'&&login()} />
        </div>
        <button className="btn btn-primary" style={{ width:'100%', justifyContent:'center', marginTop:8, padding:'11px 0' }} onClick={login} disabled={loading}>
          {loading ? 'Giriş yapılıyor...' : '→  Giriş Yap'}
        </button>
        <div style={{ textAlign:'center', marginTop:20, fontSize:11, color:'var(--txt3)' }}>
          aicallcenter.callysta.com.tr
        </div>
      </div>
    </div>
  );
}

// ── DASHBOARD ───────────────────────────────────────────
function Dashboard({ liveActive }) {
  const [stats, setStats] = useState({ today:{}, queue:{}, customers:{}, scheduler:{} });
  const [calls, setCalls] = useState([]);
  const load = async () => {
    const [s,c] = await Promise.all([axios.get('/api/calls/stats'), axios.get('/api/calls?limit=20')]);
    setStats(s.data); setCalls(c.data);
  };
  useEffect(() => { load(); const t=setInterval(load,4000); return ()=>clearInterval(t); }, []);
  const rate = stats.today?.total>0 ? Math.round((stats.today.success/stats.today.total)*100) : 0;
  const statItems = [
    {label:'BUGÜN TOPLAM', val:stats.today?.total||0, sub:'arama', color:'#c8d8e8'},
    {label:'BAŞARILI', val:stats.today?.success||0, sub:'dönüşüm', color:'var(--green)'},
    {label:'BAŞARISIZ', val:stats.today?.failed||0, sub:'ulaşılamadı', color:'var(--red)'},
    {label:'AKTİF', val:(stats.today?.active||0)+liveActive, sub:'canlı çağrı', color:'var(--amber)'},
    {label:'KUYRUKTA', val:stats.queue?.pending||0, sub:'bekleyen', color:'var(--indigo)'},
    {label:'MÜŞTERİ', val:stats.customers?.total||0, sub:'kayıt', color:'var(--cyan)'},
    {label:'BAŞARI ORANI', val:rate+'%', sub:'bugün', color:rate>50?'var(--green)':'var(--amber)'},
    {label:'DURUM', val:stats.scheduler?.running?'AKTİF':'DURDU', sub:'scheduler', color:stats.scheduler?.running?'var(--green)':'var(--red)'},
  ];
  return (
    <div>
      <div className="page-header"><div><div className="page-title">Dashboard</div><div className="page-sub">Gerçek zamanlı çağrı merkezi takibi</div></div></div>
      <div className="grid g-auto" style={{marginBottom:20}}>
        {statItems.map(({label,val,sub,color})=>(
          <div className="stat-card" key={label}>
            <div className="stat-label">{label}</div>
            <div className="stat-val" style={{color}}>{val}</div>
            <div className="stat-sub">{sub}</div>
          </div>
        ))}
      </div>
      <div className="card">
        <div className="card-title">📋 Son Çağrılar</div>
        <table>
          <thead><tr><th>Telefon</th><th>Kampanya</th><th>Sonuç</th><th>Müşteri</th><th>Saat</th></tr></thead>
          <tbody>
            {calls.length===0 ? <tr><td colSpan="5" className="empty">Henüz çağrı yok</td></tr>
              : calls.map(c=>(
                <tr key={c.id}>
                  <td className="mono">{c.phone}</td>
                  <td><span className="tag">{c.campaign_name||'-'}</span></td>
                  <td><span className={`badge ${c.outcome||c.status}`}>
                    {c.outcome==='success'?'✅ Başarılı':c.outcome==='failed'?'❌ Başarısız':c.outcome==='inbound'?'📲 Gelen':c.status==='calling'?'📞 Arıyor':c.outcome||c.status}
                  </span></td>
                  <td style={{color:'var(--txt2)',fontSize:12}}>{c.customer_name||'-'}</td>
                  <td className="mono" style={{fontSize:11,color:'var(--txt3)'}}>{fmtTime(c.started_at)}</td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ── QUEUE PAGE ───────────────────────────────────────────
function QueuePage() {
  const [campaigns,setCampaigns]=useState([]);
  const [queue,setQueue]=useState([]);
  const [selCamp,setSelCamp]=useState('');
  const [running,setRunning]=useState(false);
  const [phones,setPhones]=useState('');
  const [newCamp,setNewCamp]=useState({name:'',prompt:''});
  const [showNew,setShowNew]=useState(false);
  const [filter,setFilter]=useState('all');

  const load=async()=>{
    const url=filter==='all'?'/api/queue':`/api/queue?status=${filter}`;
    const [c,q,s]=await Promise.all([axios.get('/api/campaigns'),axios.get(url),axios.get('/api/queue/status')]);
    setCampaigns(c.data);setQueue(q.data);setRunning(s.data.running);
  };
  useEffect(()=>{load();const t=setInterval(load,3000);return()=>clearInterval(t);},[filter]);

  const createCamp=async()=>{
    if(!newCamp.name)return toast.error('İsim gerekli');
    await axios.post('/api/campaigns',newCamp);
    toast.success('Kampanya oluşturuldu');setShowNew(false);setNewCamp({name:'',prompt:''});load();
  };
  const addPhones=async()=>{
    if(!selCamp)return toast.error('Kampanya seçin');
    const list=phones.split(/[\n,;]+/).map(p=>p.trim().replace(/\D/g,'')).filter(p=>p.length>=10);
    if(!list.length)return toast.error('Geçerli numara yok');
    const r=await axios.post('/api/queue/add',{phones:list,campaignId:selCamp});
    toast.success(`${r.data.added} numara eklendi`);setPhones('');load();
  };
  const toggleRun=async()=>{
    if(running){await axios.post('/api/queue/stop');toast('⏸ Durduruldu');setRunning(false);}
    else{
      if(!selCamp)return toast.error('Kampanya seçin');
      await axios.put(`/api/campaigns/${selCamp}`,{status:'active'});
      await axios.post('/api/queue/start');toast.success('▶ Başlatıldı');setRunning(true);
    }
    load();
  };

  return (
    <div>
      <div className="page-header">
        <div><div className="page-title">Kuyruk Yönetimi</div><div className="page-sub">{queue.length} kayıt</div></div>
        <div className="actions">
          <button className="btn btn-ghost btn-sm" onClick={()=>axios.delete('/api/queue/clear/done').then(()=>{toast.success('Temizlendi');load();})}>🗑 Tamamlananları Temizle</button>
          <button className={`btn btn-sm ${running?'btn-red':'btn-green'}`} onClick={toggleRun}>{running?'⏸ Durdur':'▶ Başlat'}</button>
        </div>
      </div>
      <div className="grid g2" style={{marginBottom:16}}>
        <div className="card">
          <div className="card-title">🎯 Kampanya</div>
          <div className="fg"><label>Aktif Kampanya</label>
            <select value={selCamp} onChange={e=>setSelCamp(e.target.value)}>
              <option value="">-- Seçin --</option>
              {campaigns.map(c=><option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
          <button className="btn btn-ghost btn-sm" onClick={()=>setShowNew(!showNew)}>+ Yeni Kampanya</button>
          {showNew&&(
            <div style={{marginTop:14,padding:14,background:'var(--bg3)',borderRadius:8,border:'1px solid var(--border)'}}>
              <div className="fg"><label>Kampanya Adı</label><input value={newCamp.name} onChange={e=>setNewCamp(p=>({...p,name:e.target.value}))} /></div>
              <div className="fg"><label>AI Promptu</label><textarea value={newCamp.prompt} onChange={e=>setNewCamp(p=>({...p,prompt:e.target.value}))} /></div>
              <div style={{display:'flex',gap:8}}>
                <button className="btn btn-primary btn-sm" onClick={createCamp}>Oluştur</button>
                <button className="btn btn-ghost btn-sm" onClick={()=>setShowNew(false)}>İptal</button>
              </div>
            </div>
          )}
        </div>
        <div className="card">
          <div className="card-title">📱 Numara Ekle</div>
          <div className="fg"><label>Numaralar</label><textarea value={phones} onChange={e=>setPhones(e.target.value)} placeholder={"05XXXXXXXXX\n05XXXXXXXXX"} style={{minHeight:80}} /></div>
          <button className="btn btn-primary btn-sm" onClick={addPhones}>+ Ekle ({phones.split(/[\n,;]+/).filter(p=>p.trim().length>=10).length} numara)</button>
        </div>
      </div>
      <div className="card">
        <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:14}}>
          <div className="card-title" style={{marginBottom:0}}>📋 Kuyruk ({queue.length})</div>
          <div style={{display:'flex',gap:6}}>
            {['all','pending','calling','completed','failed'].map(f=>(
              <button key={f} className={`btn btn-xs ${filter===f?'btn-primary':'btn-ghost'}`} onClick={()=>setFilter(f)}>
                {f==='all'?'Tümü':f==='pending'?'Bekleyen':f==='calling'?'Arıyor':f==='completed'?'Tamamlandı':'Başarısız'}
              </button>
            ))}
          </div>
        </div>
        <table>
          <thead><tr><th>Telefon</th><th>Kampanya</th><th>Deneme</th><th>Durum</th><th>Eklenme</th><th></th></tr></thead>
          <tbody>
            {queue.length===0?<tr><td colSpan="6" className="empty">Kuyruk boş</td></tr>
              :queue.slice(0,200).map(q=>(
                <tr key={q.id}>
                  <td className="mono">{q.phone}</td>
                  <td><span className="tag">{q.campaign_name||'-'}</span></td>
                  <td className="mono">{q.attempt}/{q.max_attempts}</td>
                  <td><span className={`badge ${q.status}`}>{q.status}</span></td>
                  <td className="mono" style={{fontSize:11,color:'var(--txt3)'}}>{fmtTime(q.created_at)}</td>
                  <td><button className="btn btn-ghost btn-xs" onClick={()=>axios.delete(`/api/queue/${q.id}`).then(()=>load())}>✕</button></td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ── INBOUND PAGE ─────────────────────────────────────────
function InboundPage() {
  const [logs,setLogs]=useState([]);
  const [calls,setCalls]=useState([]);
  const bottomRef=useRef(null);
  const load=async()=>{
    const r=await axios.get('/api/calls?limit=50').catch(()=>({data:[]}));
    setCalls((r.data||[]).filter(c=>c.outcome==='inbound'||c.status==='inbound'));
  };
  useEffect(()=>{
    load();const t=setInterval(load,5000);
    socket.on('inbound_log',d=>{setLogs(prev=>[...prev.slice(-200),{...d,ts:new Date().toLocaleTimeString('tr-TR')}]);});
    socket.on('inbound_call',d=>{toast(`📲 Gelen: ${d.phone}`,{icon:'📲'});load();});
    return()=>{clearInterval(t);socket.off('inbound_log');socket.off('inbound_call');};
  },[]);
  useEffect(()=>{bottomRef.current?.scrollIntoView({behavior:'smooth'});},[logs]);
  return (
    <div>
      <div className="page-header">
        <div><div className="page-title">Gelen Çağrılar</div><div className="page-sub">IVR ve canlı log</div></div>
        <button className="btn btn-ghost btn-sm" onClick={()=>setLogs([])}>🗑 Temizle</button>
      </div>
      <div className="grid g2">
        <div className="card">
          <div className="card-title">📲 Son Gelen Çağrılar</div>
          <table>
            <thead><tr><th>Telefon</th><th>Tür</th><th>Saat</th></tr></thead>
            <tbody>
              {calls.length===0?<tr><td colSpan="3" className="empty">Henüz gelen çağrı yok</td></tr>
                :calls.map(c=><tr key={c.id}><td className="mono">{c.phone}</td><td><span className="badge inbound">{c.customer_note||'gelen'}</span></td><td className="mono" style={{fontSize:11,color:'var(--txt3)'}}>{fmtTime(c.started_at)}</td></tr>)}
            </tbody>
          </table>
        </div>
        <div className="card" style={{display:'flex',flexDirection:'column'}}>
          <div className="card-title">📡 Canlı Log</div>
          <div style={{flex:1,overflowY:'auto',maxHeight:300,background:'var(--bg3)',borderRadius:6,border:'1px solid var(--border)'}}>
            {logs.length===0?<div className="empty">Canlı loglar burada görünecek</div>
              :logs.map((l,i)=>(
                <div key={i} className={`log-entry ${l.type==='inbound'?'log-in':l.type==='ai'?'log-ai':'log-sys'}`}>
                  <span className="log-time">{l.ts}</span>
                  <span className="log-msg">{l.message}</span>
                </div>
              ))}
            <div ref={bottomRef}/>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── WHATSAPP PAGE ─────────────────────────────────────────
function WhatsAppPage() {
  const [convs,setConvs]=useState([]);
  const [selPhone,setSelPhone]=useState(null);
  const [msgs,setMsgs]=useState([]);
  const [draft,setDraft]=useState('');
  const [sendTo,setSendTo]=useState('');
  const [sendMsg,setSendMsg]=useState('');
  const bottomRef=useRef(null);
  const loadConvs=async()=>{const r=await axios.get('/api/whatsapp/conversations').catch(()=>({data:[]}));setConvs(r.data||[]);};
  const loadMsgs=async(phone)=>{const r=await axios.get(`/api/whatsapp/conversations/${phone}`).catch(()=>({data:[]}));setMsgs(r.data||[]);};
  useEffect(()=>{
    loadConvs();const t=setInterval(loadConvs,5000);
    socket.on('wa_message',()=>{loadConvs();if(selPhone)loadMsgs(selPhone);});
    return()=>{clearInterval(t);socket.off('wa_message');};
  },[selPhone]);
  useEffect(()=>{if(selPhone)loadMsgs(selPhone);},[selPhone]);
  useEffect(()=>{bottomRef.current?.scrollIntoView({behavior:'smooth'});},[msgs]);
  const sendDirect=async()=>{
    if(!sendTo||!sendMsg)return toast.error('Numara ve mesaj gerekli');
    await axios.post('/api/whatsapp/send',{phone:sendTo.replace(/\D/g,''),message:sendMsg});
    toast.success('Gönderildi');setSendMsg('');loadConvs();setSelPhone(sendTo.replace(/\D/g,''));
  };
  const sendReply=async()=>{
    if(!selPhone||!draft)return;
    await axios.post('/api/whatsapp/send',{phone:selPhone,message:draft});
    setDraft('');loadMsgs(selPhone);
  };
  return (
    <div>
      <div className="page-header"><div><div className="page-title">WhatsApp AI Chat</div><div className="page-sub">{convs.length} konuşma</div></div></div>
      <div className="card" style={{marginBottom:16}}>
        <div className="card-title">📤 Yeni Mesaj</div>
        <div style={{display:'flex',gap:10,alignItems:'flex-end'}}>
          <div style={{flex:'0 0 200px'}}><label>Alıcı</label><input value={sendTo} onChange={e=>setSendTo(e.target.value)} placeholder="905XXXXXXXXX"/></div>
          <div style={{flex:1}}><label>Mesaj</label><input value={sendMsg} onChange={e=>setSendMsg(e.target.value)} onKeyDown={e=>e.key==='Enter'&&sendDirect()} placeholder="Mesajınız..."/></div>
          <button className="btn btn-green btn-sm" onClick={sendDirect}>Gönder ↗</button>
        </div>
      </div>
      <div style={{display:'grid',gridTemplateColumns:'280px 1fr',gap:14,height:500}}>
        <div className="card" style={{overflow:'hidden',display:'flex',flexDirection:'column',padding:0}}>
          <div style={{padding:'12px 14px',borderBottom:'1px solid var(--border)',fontSize:11,color:'var(--txt2)',fontFamily:'IBM Plex Mono'}}>KONUŞMALAR</div>
          <div style={{flex:1,overflowY:'auto'}}>
            {convs.length===0?<div className="empty">Mesaj yok</div>:convs.map(c=>(
              <div key={c.phone} onClick={()=>setSelPhone(c.phone)} style={{padding:'10px 14px',cursor:'pointer',borderBottom:'1px solid var(--border)',background:selPhone===c.phone?'rgba(79,142,247,.1)':'transparent'}}>
                <div style={{display:'flex',justifyContent:'space-between',marginBottom:3}}><span className="mono" style={{fontSize:12,color:selPhone===c.phone?'var(--indigo)':'var(--txt)'}}>{c.phone}</span><span style={{fontSize:10,color:'var(--txt3)'}}>{c.msg_count}</span></div>
                <div style={{fontSize:11,color:'var(--txt3)',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{c.last_content}</div>
              </div>
            ))}
          </div>
        </div>
        <div className="card" style={{display:'flex',flexDirection:'column',padding:0}}>
          {!selPhone?<div className="empty" style={{display:'flex',alignItems:'center',justifyContent:'center',height:'100%'}}>Soldaki listeden bir konuşma seçin</div>:(
            <>
              <div style={{padding:'12px 14px',borderBottom:'1px solid var(--border)',display:'flex',alignItems:'center',gap:10}}>
                <span className="mono" style={{fontSize:13}}>{selPhone}</span>
                <span className="badge inbound">WhatsApp</span>
              </div>
              <div style={{flex:1,overflowY:'auto',padding:14,display:'flex',flexDirection:'column',gap:10}}>
                {msgs.map(m=>(
                  <div key={m.id} style={{display:'flex',justifyContent:m.role==='user'?'flex-start':'flex-end'}}>
                    <div style={{maxWidth:'75%',padding:'8px 12px',borderRadius:m.role==='user'?'12px 12px 12px 2px':'12px 12px 2px 12px',background:m.role==='user'?'var(--bg3)':'rgba(79,142,247,.15)',border:`1px solid ${m.role==='user'?'var(--border)':'rgba(79,142,247,.3)'}`,fontSize:13,lineHeight:1.5}}>
                      {m.content}
                      <div style={{fontSize:10,color:'var(--txt3)',marginTop:4,textAlign:'right'}}>{fmtTime(m.created_at)}</div>
                    </div>
                  </div>
                ))}
                <div ref={bottomRef}/>
              </div>
              <div style={{padding:12,borderTop:'1px solid var(--border)',display:'flex',gap:8}}>
                <input value={draft} onChange={e=>setDraft(e.target.value)} placeholder="Yanıt yazın..." onKeyDown={e=>e.key==='Enter'&&sendReply()} style={{flex:1}}/>
                <button className="btn btn-primary btn-sm" onClick={sendReply}>Gönder</button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ── WA SETUP PAGE ─────────────────────────────────────────
function WaSetupPage() {
  const [cfg,setCfg]=useState({phone_number_id:'',business_id:'',access_token:'',verify_token:'callysta_verify',chatbot_enabled:false,chatbot_source_url:'',chatbot_prompt:''});
  const [saved,setSaved]=useState(false);
  const [testing,setTesting]=useState(false);

  useEffect(()=>{
    axios.get('/api/wa-config').then(r=>{
      if(r.data) setCfg({...r.data, chatbot_enabled: !!r.data.chatbot_enabled});
    }).catch(()=>{});
  },[]);

  const save=async()=>{
    await axios.post('/api/wa-config',{...cfg,chatbot_enabled:cfg.chatbot_enabled?1:0});
    toast.success('✅ WhatsApp yapılandırması kaydedildi');
    setSaved(true);setTimeout(()=>setSaved(false),2000);
  };

  const testConnection=async()=>{
    if(!cfg.phone_number_id||!cfg.access_token) return toast.error('Phone Number ID ve Access Token gerekli');
    setTesting(true);
    try {
      const r=await fetch(`https://graph.facebook.com/v18.0/${cfg.phone_number_id}`,{headers:{Authorization:`Bearer ${cfg.access_token}`}});
      const d=await r.json();
      if(d.id) toast.success(`✅ Bağlantı başarılı: ${d.display_phone_number||d.id}`);
      else toast.error('❌ Bağlantı hatası: '+JSON.stringify(d.error?.message||d));
    } catch(e){ toast.error('Bağlantı hatası: '+e.message); }
    setTesting(false);
  };

  const genVerifyToken=()=>{
    const t='cv_'+Math.random().toString(36).slice(2,10);
    setCfg(p=>({...p,verify_token:t}));
  };

  return (
    <div>
      <div className="page-header">
        <div><div className="page-title">WhatsApp Bağlantısı</div><div className="page-sub">Meta Business API yapılandırmanız</div></div>
        <div className="actions">
          <button className="btn btn-ghost btn-sm" onClick={testConnection} disabled={testing}>{testing?'Test ediliyor...':'🔌 Bağlantıyı Test Et'}</button>
          <button className={`btn btn-sm ${saved?'btn-green':'btn-primary'}`} onClick={save}>{saved?'✅ Kaydedildi':'💾 Kaydet'}</button>
        </div>
      </div>

      <div className="grid g2" style={{marginBottom:14}}>
        <div className="card">
          <div className="card-title">📱 Numara Bağla</div>
          <div className="fg"><label>PHONE NUMBER ID</label>
            <input value={cfg.phone_number_id} onChange={e=>setCfg(p=>({...p,phone_number_id:e.target.value}))} placeholder="Meta Business'tan Phone Number ID"/>
          </div>
          <div className="fg"><label>BUSINESS ACCOUNT ID</label>
            <input value={cfg.business_id} onChange={e=>setCfg(p=>({...p,business_id:e.target.value}))} placeholder="WhatsApp Business Account ID"/>
          </div>
          <div className="fg"><label>ACCESS TOKEN</label>
            <input type="password" value={cfg.access_token} onChange={e=>setCfg(p=>({...p,access_token:e.target.value}))} placeholder="Meta API Access Token"/>
          </div>
          <div className="fg">
            <label>VERIFY TOKEN (Webhook için)</label>
            <div style={{display:'flex',gap:8}}>
              <input value={cfg.verify_token} onChange={e=>setCfg(p=>({...p,verify_token:e.target.value}))} placeholder="Webhook doğrulama tokeni" style={{flex:1}}/>
              <button className="btn btn-ghost btn-sm" onClick={genVerifyToken} style={{flexShrink:0}}>🔀 Oluştur</button>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="card-title">🤖 WhatsApp Chatbot</div>
          <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:16,padding:'12px 14px',background:'var(--bg3)',borderRadius:8,border:'1px solid var(--border)'}}>
            <div>
              <div style={{fontSize:13,fontWeight:600}}>Chatbot Aktif</div>
              <div style={{fontSize:11,color:'var(--txt3)',marginTop:2}}>Gelen mesajlara otomatik yanıt</div>
            </div>
            <label className="toggle">
              <input type="checkbox" checked={cfg.chatbot_enabled} onChange={e=>setCfg(p=>({...p,chatbot_enabled:e.target.checked}))}/>
              <span className="toggle-slider"></span>
            </label>
          </div>
          <div className="fg">
            <label>KAYNAK URL (AI buradan öğrenir)</label>
            <input value={cfg.chatbot_source_url} onChange={e=>setCfg(p=>({...p,chatbot_source_url:e.target.value}))} placeholder="https://siteniz.com/hakkimizda"/>
            <div style={{fontSize:11,color:'var(--txt3)',marginTop:4}}>Boş bırakılırsa AI genel bilgisiyle yanıt verir</div>
          </div>
          <div className="fg">
            <label>ÖZEL PROMPT (isteğe bağlı)</label>
            <textarea value={cfg.chatbot_prompt} onChange={e=>setCfg(p=>({...p,chatbot_prompt:e.target.value}))} placeholder="Sen Callysta'nın WhatsApp asistanısın. Türkçe konuş..." style={{minHeight:80}}/>
          </div>
        </div>
      </div>

      <div className="card">
        <div className="card-title">📋 Webhook Bilgileri</div>
        <div style={{display:'grid',gap:10}}>
          <div>
            <label>WEBHOOK URL</label>
            <div style={{background:'var(--bg3)',padding:'10px 14px',borderRadius:7,fontFamily:'IBM Plex Mono',fontSize:13,color:'var(--cyan)',border:'1px solid var(--border)',cursor:'pointer'}}
              onClick={()=>{navigator.clipboard.writeText('https://aicallcenter.callysta.com.tr/api/whatsapp/webhook');toast.success('Kopyalandı');}}>
              https://aicallcenter.callysta.com.tr/api/whatsapp/webhook <span style={{color:'var(--txt3)',fontSize:11}}>// tıkla kopyala</span>
            </div>
          </div>
          <div style={{padding:'12px 14px',background:'rgba(245,166,35,.05)',border:'1px solid rgba(245,166,35,.2)',borderRadius:8,fontSize:12,color:'var(--txt2)'}}>
            <strong style={{color:'var(--amber)'}}>📌 Kurulum adımları:</strong>
            <ol style={{marginTop:8,paddingLeft:18,lineHeight:2}}>
              <li>Meta Business Suite → WhatsApp → Yapılandırma → Webhook</li>
              <li>Yukarıdaki URL'yi "Callback URL" alanına girin</li>
              <li>Verify Token olarak yukarıdaki değeri girin</li>
              <li>messages webhook alanını aktive edin</li>
              <li>"Bağlantıyı Test Et" butonuna tıklayın</li>
            </ol>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── LOGS PAGE ─────────────────────────────────────────────
function LogsPage() {
  const [logs,setLogs]=useState([]);
  const [filter,setFilter]=useState('all');
  const [days,setDays]=useState('15');
  const [loading,setLoading]=useState(false);

  const load=async()=>{
    setLoading(true);
    const r=await axios.get(`/api/logs?type=${filter}&days=${days}`).catch(()=>({data:[]}));
    setLogs(r.data||[]);
    setLoading(false);
  };
  useEffect(()=>{load();},[filter,days]);

  const download=()=>{
    const csv='Tarih,Tür,Seviye,Mesaj\n'+logs.map(l=>`"${l.created_at}","${l.type}","${l.level}","${(l.message||'').replace(/"/g,'""')}"`).join('\n');
    const blob=new Blob(['\uFEFF'+csv],{type:'text/csv;charset=utf-8'});
    const url=URL.createObjectURL(blob);
    const a=document.createElement('a');a.href=url;a.download=`callysta_loglar_${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(a);a.click();document.body.removeChild(a);
    toast.success('CSV indiriliyor...');
  };

  const typeColor=t=>t==='call'?'var(--indigo)':t==='wa'?'var(--green)':t==='system'?'var(--txt3)':'var(--amber)';

  return (
    <div>
      <div className="page-header">
        <div><div className="page-title">Loglar & Kayıtlar</div><div className="page-sub">{logs.length} kayıt · Son {days} gün</div></div>
        <div className="actions">
          {['all','call','wa','system'].map(f=>(
            <button key={f} className={`btn btn-sm ${filter===f?'btn-primary':'btn-ghost'}`} onClick={()=>setFilter(f)}>
              {f==='all'?'Tümü':f==='call'?'📞 Çağrı':f==='wa'?'💬 WhatsApp':'⚙️ Sistem'}
            </button>
          ))}
          <select value={days} onChange={e=>setDays(e.target.value)} style={{width:'auto',padding:'5px 10px',fontSize:12}}>
            <option value="1">Son 1 gün</option>
            <option value="7">Son 7 gün</option>
            <option value="15">Son 15 gün</option>
          </select>
          <button className="btn btn-ghost btn-sm" onClick={download} disabled={!logs.length}>⬇ CSV İndir</button>
          <button className="btn btn-ghost btn-sm" onClick={load}>↺ Yenile</button>
        </div>
      </div>
      <div className="card">
        {loading?<div className="empty">Yükleniyor...</div>:logs.length===0?<div className="empty">Bu dönemde log bulunamadı</div>:(
          <div style={{maxHeight:600,overflowY:'auto'}}>
            <table>
              <thead><tr><th>Tarih</th><th>Tür</th><th>Seviye</th><th>Mesaj</th></tr></thead>
              <tbody>
                {logs.map((l,i)=>(
                  <tr key={i}>
                    <td className="mono" style={{fontSize:11,color:'var(--txt3)',whiteSpace:'nowrap'}}>{new Date(l.created_at).toLocaleString('tr-TR')}</td>
                    <td><span style={{display:'inline-flex',alignItems:'center',padding:'2px 8px',borderRadius:100,fontSize:11,fontFamily:'IBM Plex Mono',border:`1px solid ${typeColor(l.type)}`,color:typeColor(l.type)}}>{l.type}</span></td>
                    <td><span style={{fontSize:11,color:l.level==='error'?'var(--red)':l.level==='warn'?'var(--amber)':'var(--txt3)'}}>{l.level}</span></td>
                    <td style={{fontSize:12}}>{l.message}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

// ── CUSTOMERS PAGE ───────────────────────────────────────
function CustomersPage() {
  const [customers,setCustomers]=useState([]);
  const [total,setTotal]=useState(0);
  const [search,setSearch]=useState('');
  useEffect(()=>{
    axios.get(`/api/customers?search=${search}`).then(r=>{setCustomers(r.data.customers||[]);setTotal(r.data.total||0);});
  },[search]);
  return (
    <div>
      <div className="page-header">
        <div><div className="page-title">Müşteriler</div><div className="page-sub">{total} kayıt</div></div>
        <button className="btn btn-ghost btn-sm" onClick={()=>window.open('/api/customers/export','_blank')}>⬇ CSV</button>
      </div>
      <div className="card" style={{marginBottom:14}}>
        <input placeholder="İsim veya telefon ara..." value={search} onChange={e=>setSearch(e.target.value)}/>
      </div>
      <div className="card">
        <table>
          <thead><tr><th>Ad Soyad</th><th>Telefon</th><th>Not</th><th>Tarih</th></tr></thead>
          <tbody>
            {customers.length===0?<tr><td colSpan="4" className="empty">Kayıt yok</td></tr>
              :customers.map(c=>(
                <tr key={c.id}>
                  <td style={{fontWeight:500}}>{c.name||'-'}</td>
                  <td className="mono">{c.phone}</td>
                  <td style={{fontSize:12,color:'var(--txt2)'}}>{c.note||'-'}</td>
                  <td className="mono" style={{fontSize:11,color:'var(--txt3)'}}>{new Date(c.created_at).toLocaleDateString('tr-TR')}</td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ── SETTINGS PAGE ────────────────────────────────────────
function SettingsPage() {
  const [s,setS]=useState({concurrent_calls:'3',call_interval_seconds:'30',working_hours_start:'09:00',working_hours_end:'21:00'});
  const [opening,setOpening]=useState({message:'',voice:'tr-TR-EmelNeural',rate:'+5%',source_url:''});
  const [generating,setGenerating]=useState(false);

  useEffect(()=>{
    axios.get('/api/settings').then(r=>setS(r.data));
    axios.get('/api/settings/opening').then(r=>setOpening(r.data)).catch(()=>{});
  },[]);

  const saveSettings=async()=>{await axios.post('/api/settings',s);toast.success('✅ Ayarlar kaydedildi');};
  const saveOpening=async()=>{await axios.post('/api/settings/opening',opening);toast.success('✅ Açılış mesajı kaydedildi');};

  const generateFromUrl=async()=>{
    if(!opening.source_url)return toast.error('Kaynak URL gerekli');
    setGenerating(true);
    try{
      const r=await axios.post('/api/settings/opening/generate',{source_url:opening.source_url});
      setOpening(p=>({...p,message:r.data.message}));
      toast.success('✅ AI açılış mesajı oluşturdu!');
    }catch(e){toast.error('URL alınamadı: '+(e.response?.data?.error||e.message));}
    setGenerating(false);
  };

  const voices=['tr-TR-EmelNeural','tr-TR-AhmetNeural'];
  const rates=['-10%','-5%','0%','+5%','+10%','+15%'];

  return (
    <div>
      <div className="page-header"><div><div className="page-title">Ayarlar</div><div className="page-sub">Sistem konfigürasyonu</div></div></div>
      <div className="grid g2" style={{marginBottom:14}}>
        <div className="card">
          <div className="card-title">📞 Çağrı Ayarları</div>
          {[['Eş Zamanlı Çağrı','concurrent_calls','number'],['Çağrılar Arası (sn)','call_interval_seconds','number']].map(([l,k,t])=>(
            <div className="fg" key={k}><label>{l}</label><input type={t} value={s[k]||''} onChange={e=>setS(p=>({...p,[k]:e.target.value}))}/></div>
          ))}
          <div className="grid g2">
            <div className="fg"><label>Çalışma Başlangıç</label><input type="time" value={s.working_hours_start||''} onChange={e=>setS(p=>({...p,working_hours_start:e.target.value}))}/></div>
            <div className="fg"><label>Çalışma Bitiş</label><input type="time" value={s.working_hours_end||''} onChange={e=>setS(p=>({...p,working_hours_end:e.target.value}))}/></div>
          </div>
          <button className="btn btn-primary btn-sm" onClick={saveSettings}>💾 Kaydet</button>
        </div>
        <div className="card">
          <div className="card-title">🤖 AI Ses & Açılış Ayarları</div>
          <div className="fg"><label>SES (TTS Voice)</label>
            <select value={opening.voice} onChange={e=>setOpening(p=>({...p,voice:e.target.value}))}>
              {voices.map(v=><option key={v} value={v}>{v}</option>)}
            </select>
          </div>
          <div className="fg"><label>KONUŞMA HIZI</label>
            <select value={opening.rate} onChange={e=>setOpening(p=>({...p,rate:e.target.value}))}>
              {rates.map(r=><option key={r} value={r}>{r}</option>)}
            </select>
          </div>
          <div className="fg">
            <label>KAYNAK URL (AI otomatik mesaj oluşturur)</label>
            <div style={{display:'flex',gap:8}}>
              <input value={opening.source_url} onChange={e=>setOpening(p=>({...p,source_url:e.target.value}))} placeholder="https://siteniz.com/hakkimizda" style={{flex:1}}/>
              <button className="btn btn-ghost btn-sm" onClick={generateFromUrl} disabled={generating} style={{flexShrink:0}}>{generating?'⏳ Oluşturuluyor...':'🤖 AI Oluştur'}</button>
            </div>
            <div style={{fontSize:11,color:'var(--txt3)',marginTop:4}}>URL'den AI açılış mesajı oluşturur</div>
          </div>
          <div className="fg">
            <label>AÇILIŞ MESAJI (manuel)</label>
            <textarea value={opening.message} onChange={e=>setOpening(p=>({...p,message:e.target.value}))} placeholder="Boş bırakılırsa AI veya kaynak URL'den oluşturulur..." style={{minHeight:80}}/>
          </div>
          <button className="btn btn-primary btn-sm" onClick={saveOpening}>💾 Kaydet</button>
        </div>
      </div>
      <div className="card">
        <div className="card-title">🖥 Sistem Bilgisi</div>
        <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(160px,1fr))',gap:10}}>
          {[['Arama Altyapısı','Asterisk 20 + NetGSM SIP'],['AI Motor','Groq llama-3.3-70b'],['STT','Whisper Large v3'],['TTS','edge-tts (Türkçe)'],['Veritabanı','SQLite v3'],['WhatsApp','Meta Cloud API'],['Sunucu','Ubuntu 22.04'],['Panel','v3 Multi-User']].map(([k,v])=>(
            <div key={k} style={{background:'var(--bg3)',borderRadius:7,padding:'10px 12px',border:'1px solid var(--border)'}}>
              <div style={{fontSize:10,color:'var(--txt3)',fontFamily:'IBM Plex Mono',marginBottom:4}}>{k}</div>
              <div style={{fontSize:12,fontWeight:600}}>{v}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ── APP ROOT ─────────────────────────────────────────────
export default function App() {
  const [user,setUser]=useState(()=>{
    try{return JSON.parse(localStorage.getItem('cc_user'));}catch{return null;}
  });
  const [connected,setConnected]=useState(false);
  const [liveActive,setLiveActive]=useState(0);

  useEffect(()=>{
    if(user){
      axios.get('/api/auth/me').catch(()=>{
        localStorage.removeItem('cc_token');
        localStorage.removeItem('cc_user');
        setUser(null);
      });
    }
  },[]);

  useEffect(()=>{
    socket.on('connect',()=>setConnected(true));
    socket.on('disconnect',()=>setConnected(false));
    socket.on('call_started',()=>setLiveActive(p=>p+1));
    socket.on('call_completed',d=>{
      toast.custom(t=>(
        <div style={{background:'var(--bg2)',border:'1px solid var(--border)',borderRadius:8,padding:'10px 14px',color:'var(--txt)',fontSize:13}}>
          {d.outcome==='success'?'✅':'❌'} {d.phone}
        </div>
      ));
      setLiveActive(p=>Math.max(0,p-1));
    });
  },[]);

  const logout=()=>{
    localStorage.removeItem('cc_token');
    localStorage.removeItem('cc_user');
    setUser(null);
    toast('Çıkış yapıldı');
  };

  const handleLogin=(u)=>{
    localStorage.setItem('cc_user',JSON.stringify(u));
    setUser(u);
    toast.success(`Hoş geldiniz, ${u.display_name||u.username}! 👋`);
  };

  if(!user) return (
    <>
      <Toaster position="top-right" toastOptions={{style:{background:'var(--bg2)',color:'var(--txt)',border:'1px solid var(--border)',fontFamily:'IBM Plex Sans'}}}/>
      <LoginPage onLogin={handleLogin}/>
    </>
  );

  const navItems=[
    ['/','📊','Dashboard'],
    ['/queue','📞','Kuyruk'],
    ['/inbound','📲','Gelen Çağrılar'],
    ['/whatsapp','💬','WhatsApp Chat'],
    ['/wa-setup','🔌','WA Bağlantısı'],
    ['/customers','👥','Müşteriler'],
    ['/logs','📋','Loglar'],
    ['/settings','⚙️','Ayarlar'],
  ];

  return (
    <BrowserRouter>
      <Toaster position="top-right" toastOptions={{style:{background:'var(--bg2)',color:'var(--txt)',border:'1px solid var(--border)',fontFamily:'IBM Plex Sans'}}}/>
      <div className="layout">
        <aside className="sidebar">
          <div className="logo">
            <div className="logo-icon">📞</div>
            <div>
              <div className="logo-text">AI Çağrı</div>
              <div className="logo-sub">CALLYSTA v3</div>
            </div>
          </div>
          <div className={`conn-bar ${connected?'on':'off'}`}>
            <span>{connected?'●':'○'}</span>{connected?'Bağlı':'Bağlantı Yok'}
          </div>
          {liveActive>0&&<div className="live-pill">📞 {liveActive} aktif çağrı</div>}
          <nav className="nav">
            <div className="nav-section">Ana Menü</div>
            {navItems.map(([to,icon,label])=>(
              <NavLink key={to} to={to} end={to==='/'} className={({isActive})=>isActive?'active':''}>
                <span className="ni">{icon}</span>{label}
              </NavLink>
            ))}
          </nav>
          <div style={{padding:'10px',borderTop:'1px solid var(--border)',marginTop:'auto'}}>
            <div style={{padding:'8px 10px',marginBottom:4,background:'var(--bg3)',borderRadius:8,border:'1px solid var(--border)'}}>
              <div style={{fontSize:11,color:'var(--txt3)',fontFamily:'IBM Plex Mono'}}>KULLANICI</div>
              <div style={{fontSize:13,fontWeight:600,marginTop:2}}>{user.display_name||user.username}</div>
              <div style={{fontSize:10,color:'var(--txt3)',marginTop:1}}>{user.role==='admin'?'🛡 Admin':'👤 Kullanıcı'}</div>
            </div>
            <button onClick={logout} className="btn btn-ghost btn-sm" style={{width:'100%',justifyContent:'center',marginTop:4}}>
              ↩ Çıkış Yap
            </button>
          </div>
        </aside>
        <main className="main">
          <Routes>
            <Route path="/" element={<Dashboard liveActive={liveActive}/>}/>
            <Route path="/queue" element={<QueuePage/>}/>
            <Route path="/inbound" element={<InboundPage/>}/>
            <Route path="/whatsapp" element={<WhatsAppPage/>}/>
            <Route path="/wa-setup" element={<WaSetupPage/>}/>
            <Route path="/customers" element={<CustomersPage/>}/>
            <Route path="/logs" element={<LogsPage/>}/>
            <Route path="/settings" element={<SettingsPage/>}/>
          </Routes>
        </main>
      </div>
    </BrowserRouter>
  );
}
